import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { type ActivatedRouteSnapshot, NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

interface Crumb {
  readonly label: string;
  readonly url: string;
}

/**
 * Normaliza un label para comparar duplicados: case-insensitive y SIN diacríticos
 * ("Reporteria" vs "Reportería" deben considerarse el mismo crumb).
 */
export function normalizeLabel(label: string): string {
  return label
    .normalize('NFD')
    // Rango de combining diacritical marks (U+0300–U+036F) tras descomponer.
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

/**
 * Breadcrumbs. Auto-contenido (infra de layout, no dominio): deriva la ruta activa recorriendo
 * el arbol de snapshots y leyendo `data.breadcrumb` de cada segmento (definido en app.routes.ts
 * y en las *.routes.ts de cada feature).
 */
@Component({
    selector: 'app-breadcrumbs',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink],
    template: `
    @if (crumbs().length > 0) {
      <nav class="breadcrumbs" aria-label="Ruta de navegacion">
        <ol class="breadcrumbs__list">
          @for (crumb of crumbs(); track crumb.url; let last = $last) {
            <li class="breadcrumbs__item">
              @if (last) {
                <span aria-current="page">{{ crumb.label }}</span>
              } @else {
                <a [routerLink]="crumb.url">{{ crumb.label }}</a>
                <span class="breadcrumbs__sep" aria-hidden="true">/</span>
              }
            </li>
          }
        </ol>
      </nav>
    }
  `,
    styles: [
        `
      /* Inline (integrado a la topbar): sin fondo ni borde propios.
         Con poco ancho (mobile) los labels truncan con ellipsis en vez de
         desbordar la topbar (min-width: 0 en toda la cadena flex). */
      :host {
        display: block;
        min-width: 0;
        overflow: hidden;
      }
      .breadcrumbs__list {
        display: flex;
        gap: 6px;
        list-style: none;
        margin: 0;
        padding: 0;
        font-size: 13px;
        color: var(--pvp-color-text);
        white-space: nowrap;
        min-width: 0;
      }
      .breadcrumbs__item {
        display: flex;
        gap: 6px;
        align-items: center;
        min-width: 0;
      }
      .breadcrumbs a,
      .breadcrumbs [aria-current='page'] {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .breadcrumbs__sep {
        flex: none;
      }
      .breadcrumbs__sep {
        color: var(--pvp-color-text-subtle);
      }
      .breadcrumbs a {
        color: var(--pvp-color-text-muted);
        text-decoration: none;
        transition: color 150ms ease;
      }
      .breadcrumbs a:hover {
        color: var(--pvp-color-primary);
      }
      .breadcrumbs [aria-current='page'] {
        color: var(--pvp-color-text);
        font-weight: 500;
      }
    `,
    ]
})
export class BreadcrumbsComponent {
  private readonly router = inject(Router);

  private readonly navigation = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.routerState.snapshot.root),
      startWith(this.router.routerState.snapshot.root),
    ),
  );

  protected readonly crumbs = computed<readonly Crumb[]>(() => {
    const root = this.navigation();
    if (!root) {
      return [];
    }
    const result: Crumb[] = [];
    let url = '';
    let node: ActivatedRouteSnapshot | null = root;
    while (node) {
      const segment = node.url.map((s) => s.path).join('/');
      if (segment) {
        url += `/${segment}`;
      }
      // data PROPIO de la ruta (routeConfig), NO node.data: con
      // paramsInheritanceStrategy 'always' los hijos heredan el breadcrumb del
      // padre y se duplican los segmentos (Reservas / Reservas).
      const label = node.routeConfig?.data?.['breadcrumb'] as string | undefined;
      if (label) {
        const crumb: Crumb = { label, url: url || '/' };
        const prev = result[result.length - 1];
        // Dedupe de crumbs consecutivos con el mismo label normalizado (case/acentos-
        // insensitive): la ruta padre (app.routes) y la hija (feature *.routes) suelen
        // repetir el titulo ("Reporteria / Reportería"). Gana el ULTIMO — el de la
        // feature, que trae la ortografia correcta y la URL mas profunda.
        if (prev && normalizeLabel(prev.label) === normalizeLabel(crumb.label)) {
          result[result.length - 1] = crumb;
        } else {
          result.push(crumb);
        }
      }
      node = node.firstChild;
    }
    return result;
  });
}
